import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  useReactFlow,
  applyNodeChanges,
  applyEdgeChanges,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Paper,
  Divider,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { useApp } from '../../hooks/useApp';
import { useAuth } from '../../hooks/useAuth';
import { useAllFlowDiagrams, useFlowDiagram, useFlowDiagramMutations } from '../../hooks/useFlowDiagram';
import { newFlowNodeTemplate, newBackgroundBoxTemplate, HANDLE_POSITIONS, PROTOCOLS } from './flowConfigSchema';
import UnsFlowNode from './UnsFlowNode';
import UnsEdge from './UnsEdge';
import UnsBackgroundBox from './UnsBackgroundBox';

const nodeTypes = {
  UnsFlowNode: UnsFlowNode,
  UnsBackgroundBox: UnsBackgroundBox,
};

const edgeTypes = {
  unsedge: UnsEdge,
};

// Editable node for the editor — wraps UnsFlowNode with selection highlight
const EditorNode = ({ data, selected, ...rest }) => {
  return (
    <div style={{
      outline: selected ? '2px solid #90caf9' : 'none',
      borderRadius: '4px',
    }}>
      <UnsFlowNode data={data} {...rest} />
    </div>
  );
};

const editorNodeTypes = {
  UnsFlowNode: EditorNode,
  UnsBackgroundBox: UnsBackgroundBox,
};

// ─────────────────────────────────────────────────────────────────────────────
// Inner editor (needs ReactFlowProvider ancestor)
// ─────────────────────────────────────────────────────────────────────────────
const EditorCanvas = ({ factoryId }) => {
  const { user } = useAuth();
  const { fitView } = useReactFlow();
  const { diagram, flowNodes: firestoreFlowNodes, backgroundBoxes: firestoreBgBoxes, loading } = useFlowDiagram(factoryId);
  const {
    saveNodes,
    saveBackgroundBoxes,
    upsertDiagram,
    isPending,
  } = useFlowDiagramMutations(factoryId, user?.uid);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addNodeOpen, setAddNodeOpen] = useState(false);
  const [addBoxOpen, setAddBoxOpen] = useState(false);
  const [newNode, setNewNode] = useState({ ...newFlowNodeTemplate });
  const [newBox, setNewBox] = useState({ ...newBackgroundBoxTemplate });

  // Track the working flow data (editable copy)
  const workingFlowRef = useRef([]);
  const workingBgRef = useRef([]);
  // Guard against re-syncing from Firestore after local edits
  const initialLoadDoneRef = useRef(false);

  // Sync Firestore data into working state (only on initial load)
  useEffect(() => {
    if (!firestoreFlowNodes || firestoreFlowNodes.length === 0) return;
    if (initialLoadDoneRef.current) return; // Don't overwrite local edits
    initialLoadDoneRef.current = true;
    workingFlowRef.current = firestoreFlowNodes;
    workingBgRef.current = firestoreBgBoxes;
    rebuildReactFlowState(firestoreFlowNodes, firestoreBgBoxes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firestoreFlowNodes, firestoreBgBoxes]);

  // Reset when factory changes
  useEffect(() => {
    initialLoadDoneRef.current = false;
    workingFlowRef.current = [];
    workingBgRef.current = [];
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
  }, [factoryId]);

  const rebuildReactFlowState = useCallback((flowItems, bgBoxes) => {
    const bgNodes = (bgBoxes || []).map((box) => ({
      id: box.id,
      type: 'UnsBackgroundBox',
      position: box.position,
      data: {
        label: box.label,
        imageUrl: box.imageUrl,
        width: box.width,
        height: box.height,
      },
      style: { zIndex: -1 },
      draggable: true,
      selectable: true,
    }));

    const flowNodesList = (flowItems || []).map((item) => ({
      id: item.id,
      position: item.position,
      data: {
        ...item,
        hotTopic: null,
        onClick: () => {},
      },
      type: item.nodeType || 'UnsFlowNode',
      draggable: true,
      selectable: true,
    }));

    const edgesList = (flowItems || [])
      .filter((item) => item.source && item.target)
      .map((item) => {
        const sourceItem = flowItems.find((s) => s.source === item.source);
        const targetItem = flowItems.find((s) => s.id === item.target);
        return {
          id: `e${item.source}-${item.target}`,
          source: item.source,
          target: item.target,
          animated: true,
          label: item.protocol,
          data: {
            topic: item.topic,
            hotTopic: null,
            targetPosition: targetItem?.targetPosition || 'left',
            sourcePosition: item.sourceOverride || sourceItem?.sourcePosition || 'right',
          },
          sourceX: sourceItem?.position?.x,
          sourceY: sourceItem?.position?.y,
          targetX: targetItem?.position?.x,
          targetY: targetItem?.position?.y,
          type: 'unsedge',
          style: { stroke: '#fff' },
        };
      });

    setNodes([...bgNodes, ...flowNodesList]);
    setEdges(edgesList);
  }, []);

  // Handle node drag — update working ref positions
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));

    // Update working refs for position changes
    for (const change of changes) {
      if (change.type === 'position' && change.position) {
        const flowIdx = workingFlowRef.current.findIndex((n) => n.id === change.id);
        if (flowIdx >= 0) {
          workingFlowRef.current = [...workingFlowRef.current];
          workingFlowRef.current[flowIdx] = {
            ...workingFlowRef.current[flowIdx],
            position: change.position,
          };
        }
        const bgIdx = workingBgRef.current.findIndex((b) => b.id === change.id);
        if (bgIdx >= 0) {
          workingBgRef.current = [...workingBgRef.current];
          workingBgRef.current[bgIdx] = {
            ...workingBgRef.current[bgIdx],
            position: change.position,
          };
        }
      }
    }
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Node selection
  const onNodeClick = useCallback((_event, node) => {
    // Find the matching flow node or bg box data
    const flowItem = workingFlowRef.current.find((n) => n.id === node.id);
    const bgItem = workingBgRef.current.find((b) => b.id === node.id);
    setSelectedNode(flowItem || bgItem || null);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // ── Save all ────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    try {
      // Ensure diagram doc exists
      await upsertDiagram({
        title: diagram?.title || factoryId,
        description: diagram?.description || '',
      });
      await saveNodes(workingFlowRef.current);
      await saveBackgroundBoxes(workingBgRef.current);
      setSnackbar({ open: true, message: 'Diagram saved successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Save failed: ${err.message}`, severity: 'error' });
    }
  }, [upsertDiagram, saveNodes, saveBackgroundBoxes, diagram, factoryId]);

  // ── Update selected node field ──────────────────────────────────────────
  const updateSelectedField = useCallback((field, value) => {
    if (!selectedNode) return;

    // Update in working ref
    const flowIdx = workingFlowRef.current.findIndex((n) => n.id === selectedNode.id);
    if (flowIdx >= 0) {
      const updated = { ...workingFlowRef.current[flowIdx], [field]: value };
      workingFlowRef.current = [...workingFlowRef.current];
      workingFlowRef.current[flowIdx] = updated;
      setSelectedNode(updated);
      rebuildReactFlowState(workingFlowRef.current, workingBgRef.current);
      return;
    }

    const bgIdx = workingBgRef.current.findIndex((b) => b.id === selectedNode.id);
    if (bgIdx >= 0) {
      const updated = { ...workingBgRef.current[bgIdx], [field]: value };
      workingBgRef.current = [...workingBgRef.current];
      workingBgRef.current[bgIdx] = updated;
      setSelectedNode(updated);
      rebuildReactFlowState(workingFlowRef.current, workingBgRef.current);
    }
  }, [selectedNode, rebuildReactFlowState]);

  // ── Delete selected node ────────────────────────────────────────────────
  const handleDeleteSelected = useCallback(async () => {
    if (!selectedNode) return;
    const isFlowNode = workingFlowRef.current.some((n) => n.id === selectedNode.id);

    if (isFlowNode) {
      workingFlowRef.current = workingFlowRef.current.filter((n) => n.id !== selectedNode.id);
    } else {
      workingBgRef.current = workingBgRef.current.filter((b) => b.id !== selectedNode.id);
    }

    setSelectedNode(null);
    rebuildReactFlowState(workingFlowRef.current, workingBgRef.current);
    setSnackbar({ open: true, message: 'Node removed (save to persist)', severity: 'info' });
  }, [selectedNode, rebuildReactFlowState]);

  // ── Add new flow node ───────────────────────────────────────────────────
  const handleAddNode = useCallback(() => {
    if (!newNode.id) return;
    const node = {
      ...newFlowNodeTemplate,
      ...newNode,
      source: newNode.source || newNode.id,
      position: { x: 100, y: 100 },
    };
    workingFlowRef.current = [...workingFlowRef.current, node];
    rebuildReactFlowState(workingFlowRef.current, workingBgRef.current);
    setNewNode({ ...newFlowNodeTemplate });
    setAddNodeOpen(false);
    setSnackbar({ open: true, message: 'Node added (save to persist)', severity: 'info' });
  }, [newNode, rebuildReactFlowState]);

  // ── Add new background box ──────────────────────────────────────────────
  const handleAddBox = useCallback(() => {
    if (!newBox.id) return;
    const box = {
      ...newBackgroundBoxTemplate,
      ...newBox,
      position: { x: 0, y: 0 },
    };
    workingBgRef.current = [...workingBgRef.current, box];
    rebuildReactFlowState(workingFlowRef.current, workingBgRef.current);
    setNewBox({ ...newBackgroundBoxTemplate });
    setAddBoxOpen(false);
    setSnackbar({ open: true, message: 'Background box added (save to persist)', severity: 'info' });
  }, [newBox, rebuildReactFlowState]);

  // All flow node IDs for the target dropdown
  const nodeIdOptions = useMemo(
    () => workingFlowRef.current.map((n) => n.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes]
  );

  useEffect(() => {
    if (!loading && nodes.length > 0) {
      setTimeout(() => fitView({ padding: 0.2 }), 100);
    }
  }, [loading, nodes.length, fitView]);

  if (loading) {
    return (
      <Box sx={{ color: 'white', textAlign: 'center', pt: 4 }}>
        Loading diagram...
      </Box>
    );
  }

  const isFlowNode = selectedNode && workingFlowRef.current.some((n) => n.id === selectedNode.id);
  const isBgBox = selectedNode && workingBgRef.current.some((b) => b.id === selectedNode.id);

  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
      {/* Canvas */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={editorNodeTypes}
          edgeTypes={edgeTypes}
          fitView
          style={{ background: '#0a1929' }}
        />

        {/* Floating toolbar */}
        <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 10, left: 10, zIndex: 10 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isPending}
            sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' } }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddNodeOpen(true)}
            sx={{ borderColor: '#90caf9', color: '#90caf9', '&:hover': { borderColor: '#fff', color: '#fff' } }}
          >
            Add Node
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddBoxOpen(true)}
            sx={{ borderColor: '#90caf9', color: '#90caf9', '&:hover': { borderColor: '#fff', color: '#fff' } }}
          >
            Add Box
          </Button>
        </Stack>
      </Box>

      {/* Properties panel */}
      <Paper
        sx={{
          width: 320,
          minWidth: 320,
          height: '100%',
          overflow: 'auto',
          bgcolor: '#0d2137',
          color: 'white',
          p: 2,
          borderLeft: '1px solid #1e3a5f',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Properties
        </Typography>
        <Divider sx={{ borderColor: '#1e3a5f', mb: 2 }} />

        {!selectedNode ? (
          <Typography variant="body2" sx={{ color: '#aaa' }}>
            Click a node to edit its properties
          </Typography>
        ) : isFlowNode ? (
          <Stack spacing={2}>
            <Chip label="Flow Node" color="primary" size="small" />
            <TextField
              label="ID"
              size="small"
              fullWidth
              value={selectedNode.id}
              disabled
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <TextField
              label="Image URL"
              size="small"
              fullWidth
              value={selectedNode.img || ''}
              onChange={(e) => updateSelectedField('img', e.target.value)}
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <TextField
              label="Topic (highlight keyword)"
              size="small"
              fullWidth
              value={Array.isArray(selectedNode.topic) ? selectedNode.topic.join(', ') : (selectedNode.topic || '')}
              onChange={(e) => {
                const val = e.target.value;
                updateSelectedField('topic', val.includes(',') ? val.split(',').map((s) => s.trim()) : val);
              }}
              helperText="Comma-separated for multiple"
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
              FormHelperTextProps={{ sx: { color: '#888' } }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: '#aaa' }}>Protocol</InputLabel>
              <Select
                value={selectedNode.protocol || ''}
                label="Protocol"
                onChange={(e) => updateSelectedField('protocol', e.target.value || null)}
                sx={{ color: 'white' }}
              >
                <MenuItem value="">None</MenuItem>
                {PROTOCOLS.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: '#aaa' }}>Target</InputLabel>
              <Select
                value={selectedNode.target || ''}
                label="Target"
                onChange={(e) => updateSelectedField('target', e.target.value || null)}
                sx={{ color: 'white' }}
              >
                <MenuItem value="">None</MenuItem>
                {nodeIdOptions.filter((id) => id !== selectedNode.id).map((id) => (
                  <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: '#aaa' }}>Source Position</InputLabel>
              <Select
                value={selectedNode.sourcePosition || 'right'}
                label="Source Position"
                onChange={(e) => updateSelectedField('sourcePosition', e.target.value)}
                sx={{ color: 'white' }}
              >
                {HANDLE_POSITIONS.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ color: '#aaa' }}>Target Position</InputLabel>
              <Select
                value={selectedNode.targetPosition || ''}
                label="Target Position"
                onChange={(e) => updateSelectedField('targetPosition', e.target.value || null)}
                sx={{ color: 'white' }}
              >
                <MenuItem value="">None</MenuItem>
                {HANDLE_POSITIONS.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Position X"
              size="small"
              type="number"
              fullWidth
              value={selectedNode.position?.x ?? 0}
              onChange={(e) => updateSelectedField('position', { ...selectedNode.position, x: Number(e.target.value) })}
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <TextField
              label="Position Y"
              size="small"
              type="number"
              fullWidth
              value={selectedNode.position?.y ?? 0}
              onChange={(e) => updateSelectedField('position', { ...selectedNode.position, y: Number(e.target.value) })}
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSelected}
            >
              Delete Node
            </Button>
          </Stack>
        ) : isBgBox ? (
          <Stack spacing={2}>
            <Chip label="Background Box" color="secondary" size="small" />
            <TextField
              label="ID"
              size="small"
              fullWidth
              value={selectedNode.id}
              disabled
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <TextField
              label="Label"
              size="small"
              fullWidth
              value={selectedNode.label || ''}
              onChange={(e) => updateSelectedField('label', e.target.value)}
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <TextField
              label="Image URL"
              size="small"
              fullWidth
              value={selectedNode.imageUrl || ''}
              onChange={(e) => updateSelectedField('imageUrl', e.target.value)}
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <TextField
              label="Width"
              size="small"
              type="number"
              fullWidth
              value={selectedNode.width ?? 200}
              onChange={(e) => updateSelectedField('width', Number(e.target.value))}
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <TextField
              label="Height"
              size="small"
              type="number"
              fullWidth
              value={selectedNode.height ?? 200}
              onChange={(e) => updateSelectedField('height', Number(e.target.value))}
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <TextField
              label="Position X"
              size="small"
              type="number"
              fullWidth
              value={selectedNode.position?.x ?? 0}
              onChange={(e) => updateSelectedField('position', { ...selectedNode.position, x: Number(e.target.value) })}
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <TextField
              label="Position Y"
              size="small"
              type="number"
              fullWidth
              value={selectedNode.position?.y ?? 0}
              onChange={(e) => updateSelectedField('position', { ...selectedNode.position, y: Number(e.target.value) })}
              InputProps={{ sx: { color: 'white' } }}
              InputLabelProps={{ sx: { color: '#aaa' } }}
            />
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSelected}
            >
              Delete Box
            </Button>
          </Stack>
        ) : null}
      </Paper>

      {/* Add Node Dialog */}
      <Dialog open={addNodeOpen} onClose={() => setAddNodeOpen(false)}>
        <DialogTitle>Add Flow Node</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1, minWidth: 300 }}>
            <TextField
              label="Node ID"
              size="small"
              fullWidth
              required
              value={newNode.id}
              onChange={(e) => setNewNode((prev) => ({ ...prev, id: e.target.value, source: e.target.value }))}
            />
            <TextField
              label="Image URL"
              size="small"
              fullWidth
              value={newNode.img}
              onChange={(e) => setNewNode((prev) => ({ ...prev, img: e.target.value }))}
            />
            <TextField
              label="Topic"
              size="small"
              fullWidth
              value={newNode.topic}
              onChange={(e) => setNewNode((prev) => ({ ...prev, topic: e.target.value }))}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Protocol</InputLabel>
              <Select
                value={newNode.protocol || ''}
                label="Protocol"
                onChange={(e) => setNewNode((prev) => ({ ...prev, protocol: e.target.value || null }))}
              >
                <MenuItem value="">None</MenuItem>
                {PROTOCOLS.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Target</InputLabel>
              <Select
                value={newNode.target || ''}
                label="Target"
                onChange={(e) => setNewNode((prev) => ({ ...prev, target: e.target.value || null }))}
              >
                <MenuItem value="">None</MenuItem>
                {nodeIdOptions.map((id) => (
                  <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNodeOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddNode} disabled={!newNode.id}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add Background Box Dialog */}
      <Dialog open={addBoxOpen} onClose={() => setAddBoxOpen(false)}>
        <DialogTitle>Add Background Box</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1, minWidth: 300 }}>
            <TextField
              label="Box ID"
              size="small"
              fullWidth
              required
              value={newBox.id}
              onChange={(e) => setNewBox((prev) => ({ ...prev, id: e.target.value }))}
            />
            <TextField
              label="Label"
              size="small"
              fullWidth
              value={newBox.label}
              onChange={(e) => setNewBox((prev) => ({ ...prev, label: e.target.value }))}
            />
            <TextField
              label="Image URL"
              size="small"
              fullWidth
              value={newBox.imageUrl || ''}
              onChange={(e) => setNewBox((prev) => ({ ...prev, imageUrl: e.target.value }))}
            />
            <TextField
              label="Width"
              size="small"
              type="number"
              fullWidth
              value={newBox.width}
              onChange={(e) => setNewBox((prev) => ({ ...prev, width: Number(e.target.value) }))}
            />
            <TextField
              label="Height"
              size="small"
              type="number"
              fullWidth
              value={newBox.height}
              onChange={(e) => setNewBox((prev) => ({ ...prev, height: Number(e.target.value) }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBoxOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddBox} disabled={!newBox.id}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────────────────────────────────────
const ArchitectureEditor = () => {
  const { user } = useAuth();
  const { combinedAssets } = useApp();
  const { diagrams } = useAllFlowDiagrams();
  const [selectedFactory, setSelectedFactory] = useState('');

  // Build factory options from both combinedAssets (unfiltered) and existing diagrams
  const factoryOptions = useMemo(() => {
    const diagramIds = new Set(diagrams.map((d) => d.id));
    const assetMap = new Map();

    // Add all factories with unsConfig enabled or existing diagrams
    for (const a of (combinedAssets || [])) {
      if (a.Type === 'factory' && (a.unsConfig?.enabled || diagramIds.has(a.id))) {
        assetMap.set(a.id, { id: a.id, label: a.Title || a.Heading || a.id });
      }
    }

    // Also include any diagrams that don't have a matching asset (edge case)
    for (const d of diagrams) {
      if (!assetMap.has(d.id)) {
        assetMap.set(d.id, { id: d.id, label: d.title || d.id });
      }
    }

    return Array.from(assetMap.values());
  }, [combinedAssets, diagrams]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0a1929',
        color: 'white',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #1e3a5f' }}>
        <Typography variant="h5">Architecture Diagram Editor</Typography>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel sx={{ color: '#aaa' }}>Select Factory</InputLabel>
          <Select
            value={selectedFactory}
            label="Select Factory"
            onChange={(e) => setSelectedFactory(e.target.value)}
            sx={{
              color: 'white',
              '.MuiOutlinedInput-notchedOutline': { borderColor: '#1e3a5f' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#90caf9' },
              '.MuiSvgIcon-root': { color: '#aaa' },
            }}
          >
            {factoryOptions.map((f) => (
              <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Editor area */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {selectedFactory ? (
          <ReactFlowProvider>
            <EditorCanvas factoryId={selectedFactory} />
          </ReactFlowProvider>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="h6" sx={{ color: '#90caf9' }}>
              Select a factory to edit its architecture diagram
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ArchitectureEditor;
